import { ref } from "vue";
import CloudStorageService, { AUTHENTICATION_STATUS } from "../services/cloudStorageService.js";

const cloudStorageService = new CloudStorageService();

// Module-level singleton — the connected-storages list is shared by
// AddModal.vue and anything else that needs it, same pattern as the rest of
// v2's shared lists (useDocs.js, useProjectsByTab.js, etc.).
const storages = ref([]);
const isLoading = ref(false);
let hasLoadedOnce = false;

// The OAuth callback page (src/views/auth/auth-callbacks/OAuth2StorageProviderCallback.vue,
// a shared app-wide route, not v2-specific) sets this in localStorage right
// before returning here, so whichever UI reopens next can auto-select the
// storage that was just connected. Read once at module load (page boot),
// before any component's onMounted runs, then cleared from localStorage —
// the in-memory ref stays set until a consumer explicitly clears it.
const pendingProvider = ref(localStorage.getItem("cloudStorage2Open"));
if (pendingProvider.value) localStorage.removeItem("cloudStorage2Open");

export function useCloudStorage() {
  async function load(force = false) {
    if (hasLoadedOnce && !force) return;
    hasLoadedOnce = true;
    isLoading.value = true;
    try {
      const list = await cloudStorageService.getAll();
      storages.value = [...list].sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt));
    } finally {
      isLoading.value = false;
    }
  }

  // Starts the OAuth redirect flow for a provider. Passing `existingStorage`
  // re-authenticates an expired connection in place instead of adding a new one.
  function connect(provider, existingStorage = null) {
    return cloudStorageService.connect(provider, existingStorage);
  }

  async function rename(storage, name) {
    await cloudStorageService.rename(storage.id, name);
    storage.name = name;
  }

  async function remove(storage) {
    await cloudStorageService.remove(storage.id);
    storages.value = storages.value.filter((s) => s.id !== storage.id);
  }

  function clearPendingProvider() {
    pendingProvider.value = null;
  }

  return {
    storages, isLoading, pendingProvider, AUTHENTICATION_STATUS,
    load, connect, rename, remove, clearPendingProvider,
  };
}
